import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PersonDto } from '../services/api';
import { getSearchSummary } from '../utils/canvasSearch';

type MobileCanvasHUDProps = {
  isBlankCanvas: boolean;
  selectedPerson: PersonDto | null;
  canAddPeople: boolean;
  canCreateRelationships: boolean;
  isAiEnabled: boolean;
  showGrid: boolean;
  snapGrid: boolean;
  searchQuery: string;
  activeSearchQuery: string;
  activeSearchCount: number;
  searchResultIndex: number;
  onSearchChange: (query: string) => void;
  onSearchClear: () => void;
  onSearchStep: (delta: number) => void;
  onAddPerson: () => void;
  onAddRelationship: () => void;
  onFit: () => void;
  onOpenMembers: () => void;
  onOpenAi: () => void;
  onToggleGrid: () => void;
  onToggleSnap: () => void;
};

const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="11" cy="11" r="7.5" />
    <path d="M20 20l-4-4" />
  </svg>
);

const FitIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 8V5a1 1 0 011-1h3" />
    <path d="M16 4h3a1 1 0 011 1v3" />
    <path d="M20 16v3a1 1 0 01-1 1h-3" />
    <path d="M8 20H5a1 1 0 01-1-1v-3" />
    <rect x="8" y="8" width="8" height="8" rx="1" />
  </svg>
);

const MembersIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M9 11a3.5 3.5 0 100-7 3.5 3.5 0 000 7zM16.5 12a3 3 0 100-6 3 3 0 000 6zM3.5 19.5A4.5 4.5 0 018 15h2a4.5 4.5 0 014.5 4.5V20h-11z" />
    <path d="M13.5 19.8v-.3A5.8 5.8 0 0012 15.5h2.2a4.3 4.3 0 014.3 4.3z" />
  </svg>
);

const AiIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3l1.9 4.9L19 10l-5.1 2.1L12 17l-1.9-4.9L5 10l5.1-2.1L12 3z" />
    <path d="M19 14l.8 2 2.2.8-2.2.8-.8 2-.8-2-2.2-.8 2.2-.8.8-2z" />
  </svg>
);

const AddPersonIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" aria-hidden="true">
    <path d="M12 5v14" />
    <path d="M5 12h14" />
  </svg>
);

const AddRelationshipIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M10.5 13.5l3-3" />
    <path d="M8.2 16.8l-1.4 1.4a3.25 3.25 0 01-4.6-4.6l3-3a3.25 3.25 0 014.6 0" />
    <path d="M14.4 7.4l1.4-1.4a3.25 3.25 0 114.6 4.6l-3 3a3.25 3.25 0 01-4.6 0" />
  </svg>
);

const ChevronUpIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M18 15l-6-6-6 6" />
  </svg>
);

const MoreIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <circle cx="5" cy="12" r="1.8" />
    <circle cx="12" cy="12" r="1.8" />
    <circle cx="19" cy="12" r="1.8" />
  </svg>
);

export const MobileCanvasHUD: React.FC<MobileCanvasHUDProps> = ({
  isBlankCanvas,
  selectedPerson,
  canAddPeople,
  canCreateRelationships,
  isAiEnabled,
  showGrid,
  snapGrid,
  searchQuery,
  activeSearchQuery,
  activeSearchCount,
  searchResultIndex,
  onSearchChange,
  onSearchClear,
  onSearchStep,
  onAddPerson,
  onAddRelationship,
  onFit,
  onOpenMembers,
  onOpenAi,
  onToggleGrid,
  onToggleSnap,
}) => {
  const { t } = useTranslation();
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [fabTrayOpen, setFabTrayOpen] = useState(false);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const searchPanelRef = useRef<HTMLDivElement | null>(null);
  const overflowMenuRef = useRef<HTMLDivElement | null>(null);
  const overflowTriggerRef = useRef<HTMLButtonElement | null>(null);
  const canShowFabTray = Boolean(selectedPerson && canCreateRelationships);

  useEffect(() => {
    if (!mobileSearchOpen) {
      return;
    }

    searchInputRef.current?.focus();
  }, [mobileSearchOpen]);

  useEffect(() => {
    setFabTrayOpen(canShowFabTray);
  }, [canShowFabTray]);

  useEffect(() => {
    if (!overflowOpen) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!target) {
        return;
      }

      if (overflowMenuRef.current?.contains(target) || overflowTriggerRef.current?.contains(target)) {
        return;
      }

      setOverflowOpen(false);
    };

    window.addEventListener('pointerdown', handlePointerDown);
    return () => window.removeEventListener('pointerdown', handlePointerDown);
  }, [overflowOpen]);

  const searchSummary = useMemo(() => {
    if (!activeSearchQuery.trim()) {
      return '';
    }

    return getSearchSummary(activeSearchCount, activeSearchQuery);
  }, [activeSearchCount, activeSearchQuery]);

  return (
    <div
      data-testid="mobile-canvas-hud"
      className="pointer-events-none absolute inset-x-0 bottom-0 z-20 md:hidden"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      <div className="pointer-events-none absolute bottom-[6.25rem] right-4 flex flex-col items-end gap-3">
        {overflowOpen && (
          <div
            ref={overflowMenuRef}
            data-testid="mobile-overflow-menu"
            className="pointer-events-auto flex min-w-[12rem] flex-col gap-2 rounded-2xl border border-[#d9d3c8] bg-[rgba(255,247,238,0.96)] p-2 shadow-[0_18px_36px_rgba(95,83,58,0.18)] backdrop-blur-sm"
          >
            <button
              type="button"
              data-testid="mobile-overflow-members-button"
              onClick={() => {
                onOpenMembers();
                setOverflowOpen(false);
              }}
              className="flex min-h-[44px] items-center justify-between rounded-xl px-3 py-2 text-left text-sm font-medium text-[#4b5563] transition hover:bg-[#efe9db]"
            >
              <span>{t('toolbar.members', { ns: 'canvas' })}</span>
              <MembersIcon />
            </button>
            <button
              type="button"
              data-testid="mobile-toggle-grid-button"
              onClick={() => {
                onToggleGrid();
                setOverflowOpen(false);
              }}
              className="flex min-h-[44px] items-center justify-between rounded-xl px-3 py-2 text-left text-sm font-medium text-[#4b5563] transition hover:bg-[#efe9db]"
            >
              <span>{showGrid ? t('toolbar.hideGrid', { ns: 'canvas' }) : t('toolbar.showGrid', { ns: 'canvas' })}</span>
              <span className="text-xs text-[#7C766F]">{showGrid ? 'On' : 'Off'}</span>
            </button>
            <button
              type="button"
              data-testid="mobile-toggle-snap-button"
              onClick={() => {
                onToggleSnap();
                setOverflowOpen(false);
              }}
              className="flex min-h-[44px] items-center justify-between rounded-xl px-3 py-2 text-left text-sm font-medium text-[#4b5563] transition hover:bg-[#efe9db]"
            >
              <span>{snapGrid ? t('toolbar.snapOn', { ns: 'canvas' }) : t('toolbar.snapOff', { ns: 'canvas' })}</span>
              <span className="text-xs text-[#7C766F]">{snapGrid ? 'On' : 'Off'}</span>
            </button>
          </div>
        )}
        {fabTrayOpen && (
          <button
            type="button"
            data-testid="mobile-add-relationship-button"
            onClick={onAddRelationship}
            className="pointer-events-auto flex min-h-[44px] items-center gap-2 rounded-2xl border border-[#d6dfce] bg-[rgba(255,250,243,0.96)] px-4 py-3 text-sm font-semibold text-[#435635] shadow-[0_16px_32px_rgba(67,86,53,0.18)] backdrop-blur-sm transition hover:-translate-y-0.5"
          >
            <AddRelationshipIcon />
            <span>{t('toolbar.addRelation', { ns: 'canvas' })}</span>
          </button>
        )}
        {canShowFabTray && (
          <button
            type="button"
            aria-label={t('toolbar.addRelation', { ns: 'canvas' })}
            onClick={() => setFabTrayOpen((current) => !current)}
            className="pointer-events-auto flex h-10 w-10 items-center justify-center rounded-full border border-[#d9d3c8] bg-[rgba(255,247,238,0.92)] text-[#675f54] shadow-[0_12px_24px_rgba(95,83,58,0.16)] backdrop-blur-sm"
          >
            <ChevronUpIcon />
          </button>
        )}
        {canAddPeople && (
          <button
            type="button"
            data-testid="mobile-add-person-button"
            title={t('toolbar.addPerson', { ns: 'canvas' })}
            aria-label={t('toolbar.addPerson', { ns: 'canvas' })}
            onClick={onAddPerson}
            className="pointer-events-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-[#bfd7f2] to-[#97bee7] text-white shadow-[0_18px_32px_rgba(151,190,231,0.42)] transition hover:-translate-y-0.5"
          >
            <AddPersonIcon />
          </button>
        )}
      </div>

      <div className="pointer-events-none px-4 pb-4">
        <div className="mx-auto flex max-w-[28rem] items-end justify-center">
          <div
            ref={searchPanelRef}
            className="pointer-events-auto flex min-h-[56px] w-full items-center gap-2 rounded-[28px] border border-[rgba(226,221,216,0.8)] bg-[rgba(249,247,244,0.82)] px-3 py-2 text-[#1A1714] shadow-[0_16px_36px_rgba(95,83,58,0.16)] backdrop-blur-md"
          >
            {!isBlankCanvas && (
              mobileSearchOpen ? (
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <button
                    type="button"
                    aria-label={t('toolbar.findPerson', { ns: 'canvas' })}
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[#7C766F]"
                    onClick={() => searchInputRef.current?.focus()}
                  >
                    <SearchIcon />
                  </button>
                  <div className="min-w-0 flex-1">
                    <input
                      ref={searchInputRef}
                      type="search"
                      data-testid="mobile-canvas-search-input"
                      placeholder={t('toolbar.findPersonPlaceholder', { ns: 'canvas' })}
                      aria-label={t('toolbar.findPerson', { ns: 'canvas' })}
                      value={searchQuery}
                      onChange={(event) => onSearchChange(event.target.value)}
                      onBlur={(event) => {
                        const nextFocused = event.relatedTarget as Node | null;
                        if (nextFocused && searchPanelRef.current?.contains(nextFocused)) {
                          return;
                        }

                        if (!searchQuery.trim()) {
                          setMobileSearchOpen(false);
                        }
                      }}
                      className="w-full bg-transparent text-sm outline-none placeholder:text-[#8D867E]"
                    />
                    {searchSummary && (
                      <div className="mt-1 flex items-center gap-2 text-[11px]">
                        <span className={activeSearchCount === 0 ? 'text-[#A32D2D]' : 'text-[#4f5d2f]'}>
                          {searchSummary}
                        </span>
                        {activeSearchCount > 1 && (
                          <span className="text-[#6B6560]">
                            {searchResultIndex + 1}/{activeSearchCount}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  {activeSearchCount > 1 && (
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        data-testid="mobile-canvas-search-prev"
                        aria-label={t('toolbar.prevResult', { ns: 'canvas' })}
                        onClick={() => onSearchStep(-1)}
                        className="flex h-10 w-10 items-center justify-center rounded-full text-[#7C766F] transition hover:bg-[#EFEAE3]"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M15 18l-6-6 6-6" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        data-testid="mobile-canvas-search-next"
                        aria-label={t('toolbar.nextResult', { ns: 'canvas' })}
                        onClick={() => onSearchStep(1)}
                        className="flex h-10 w-10 items-center justify-center rounded-full text-[#7C766F] transition hover:bg-[#EFEAE3]"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M9 6l6 6-6 6" />
                        </svg>
                      </button>
                    </div>
                  )}
                  <button
                    type="button"
                    data-testid="mobile-canvas-search-close"
                    aria-label={t('toolbar.clearSearch', { ns: 'canvas' })}
                    onClick={() => {
                      onSearchClear();
                      setMobileSearchOpen(false);
                    }}
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[#7C766F] transition hover:bg-[#EFEAE3]"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                      <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  data-testid="mobile-canvas-search-trigger"
                  aria-label={t('toolbar.findPerson', { ns: 'canvas' })}
                  onClick={() => setMobileSearchOpen(true)}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[#7C766F] transition hover:bg-[#EFEAE3]"
                >
                  <SearchIcon />
                </button>
              )
            )}

            {!isBlankCanvas && <div className="h-7 w-px bg-[rgba(141,134,126,0.24)]" />}

            {!isBlankCanvas && (
              <button
                type="button"
                data-testid="mobile-fit-button"
                title={t('toolbar.fitAll', { ns: 'canvas' })}
                aria-label={t('toolbar.fitAll', { ns: 'canvas' })}
                onClick={onFit}
                className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full text-[#4f5d2f] transition hover:bg-[rgba(143,163,107,0.14)]"
              >
                <FitIcon />
              </button>
            )}

            {isAiEnabled && (
              <button
                type="button"
                data-testid="mobile-ai-button"
                title="AI"
                aria-label="AI"
                onClick={onOpenAi}
                className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full text-[#5f3d8c] transition hover:bg-[rgba(191,167,235,0.14)]"
              >
                <AiIcon />
              </button>
            )}
            {!isBlankCanvas && (
              <button
                ref={overflowTriggerRef}
                type="button"
                data-testid="mobile-overflow-trigger"
                aria-label="More canvas actions"
                onClick={() => setOverflowOpen((current) => !current)}
                className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full text-[#6B6560] transition hover:bg-[rgba(141,134,126,0.12)]"
              >
                <MoreIcon />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
